import React, { useState, useMemo, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, ChevronUp } from 'lucide-react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import FilterDropdown from '../FilterDropdown';
import FilterModal from '../FilterModal';
import ChartHelpButton from '../ChartHelpButton';
import { getChartColor, getCleanOptions, getSmartYDomain, fobCascadeOptions, pickBestFobCombination,
  hidesPlaceholders, initCap, characteristicReader, characteristicFilterOptionKey, readRowField } from '../../utils/analyticsHelpers';
import { commoditySizeRules } from './pulse/index.js';
import { useChartFontSize } from '../../context/ChartFontSizeContext';
import { useTheme } from '../../../context/ThemeContext';
import { getChartTheme } from '../../utils/chartTheme';
import { getLegendWrapperStyle } from './chartStyleUtils';
import { usePersistWidgetSnapshot } from '../../hooks/usePersistWidgetSnapshot';
import { useWidgetOptionsPersistence } from '../../hooks/useWidgetOptionsPersistence';
import { FobByOriginTooltipBody } from './ChartTooltipContent';
import { useChartTooltipTrigger } from './useChartTooltipTrigger';
import { useIsBelowLg, getResponsiveChartMargin } from '../../hooks/useMediaQuery';
import { singleSelectionLabel, toggleMultiValue, shouldNameSeriesForMulti, applyMultiOrAllFilter } from '../../../utils/filterLabels';
import { multiSelectDropdownLabel } from './terminalPricePulseTableUtils';

const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const FOB_MULTI_KEYS = new Set(['variety', 'sizeGroup', 'size', 'grade', 'storage', 'condition', 'environment', 'quality', 'appearance', 'properties']);

/**
 * Helper: Process chart data to add solid and gap line data keys for each origin
 * - originName_solid: data for solid lines
 * - originName_gap_N: data for dashed gap connector N (only the two endpoints have values)
 *
 * Gap threshold: 2+ months apart (or 8+ weeks apart)
 */
const processChartDataForGaps = (chartData, origins, granularity) => {
  if (!chartData || chartData.length === 0) return { data: chartData, gapKeys: {} };

  // Gap threshold: 2 months or 8 weeks
  const gapThreshold = granularity === 'weekly' ? 8 : 2;

  // Deep clone the data
  const result = chartData.map(d => ({ ...d }));
  const gapKeys = {}; // { origin: ['origin_gap_0', 'origin_gap_1', ...] }

  origins.forEach(origin => {
    gapKeys[origin] = [];

    // Find all indices with data for this origin
    const dataPoints = [];
    result.forEach((d, idx) => {
      if (d[origin] !== undefined && d[origin] !== null) {
        dataPoints.push({ idx, periodNum: d.periodNum, value: d[origin] });
      }
    });

    // Process each data point
    let gapCount = 0;
    for (let i = 0; i < dataPoints.length; i++) {
      const current = dataPoints[i];

      if (i === 0) {
        // First point - add to solid
        result[current.idx][`${origin}_solid`] = current.value;
      } else {
        const prev = dataPoints[i - 1];
        const periodGap = current.periodNum - prev.periodNum;

        if (periodGap >= gapThreshold) {
          // Gap >= threshold - need dashed connector
          const gapKey = `${origin}_gap_${gapCount}`;
          gapKeys[origin].push(gapKey);

          // Set gap connector endpoints
          result[prev.idx][gapKey] = prev.value;
          result[current.idx][gapKey] = current.value;

          // Current point starts a new solid segment
          result[current.idx][`${origin}_solid`] = current.value;

          gapCount++;
        } else {
          // Within threshold - continue solid line
          result[current.idx][`${origin}_solid`] = current.value;
        }
      }
    }
  });

  return { data: result, gapKeys };
};

/**
 * FOB Price by Origin Line Chart
 * Uses cascading filters: Year → Origins → Type → Variety → Size → Grade → Package → ...
 */
const FOBPriceByOriginChart = ({
  commodity = '',
  priceData = [],
  origins = [],
  filterOptions = null,
  years = [],
}) => {
  const { isReady: analyticsPrefsReady } = useWidgetOptionsPersistence('fob-price-by-origin');
  const { t } = useTranslation();
  const { fontSize } = useChartFontSize();
  const sizeRules = commoditySizeRules(commodity);
  const readSize = characteristicReader('size', commodity);
  const sizeFoKey = characteristicFilterOptionKey('size', commodity);
  const { isDark } = useTheme();
  const theme = getChartTheme(isDark);
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  const [selectedYear, setSelectedYear] = useState(null);
  const [granularity, setGranularity] = useState('monthly');
  const [selectedOrigins, setSelectedOrigins] = useState([]);

  // Modal states
  const [yearModalOpen, setYearModalOpen] = useState(false);
  const [originModalOpen, setOriginModalOpen] = useState(false);
  const [typeModalOpen, setTypeModalOpen] = useState(false);
  const [varietyModalOpen, setVarietyModalOpen] = useState(false);
  const [sizeModalOpen, setSizeModalOpen] = useState(false);
  const [gradeModalOpen, setGradeModalOpen] = useState(false);
  const [packageModalOpen, setPackageModalOpen] = useState(false);
  const [storageModalOpen, setStorageModalOpen] = useState(false);
  const [conditionModalOpen, setConditionModalOpen] = useState(false);
  const [environmentModalOpen, setEnvironmentModalOpen] = useState(false);
  const [qualityModalOpen, setQualityModalOpen] = useState(false);
  const [appearanceModalOpen, setAppearanceModalOpen] = useState(false);
  const [propertiesModalOpen, setPropertiesModalOpen] = useState(false);

  // Product characteristic filters (single-select)
  const [selectedType, setSelectedType] = useState('Conventional');
  const [selectedVariety, setSelectedVariety] = useState([]);
  const [selectedSize, setSelectedSize] = useState([]);
  const [selectedSizeGroup, setSelectedSizeGroup] = useState([]);
  const [sizeGroupModalOpen, setSizeGroupModalOpen] = useState(false);
  const gatedCascade = hidesPlaceholders(commodity);
  const [selectedGrade, setSelectedGrade] = useState([]);
  const [selectedPackage, setSelectedPackage] = useState('');
  const [selectedStorage, setSelectedStorage] = useState([]);
  const [selectedCondition, setSelectedCondition] = useState([]);
  const [selectedEnvironment, setSelectedEnvironment] = useState([]);
  const [selectedQuality, setSelectedQuality] = useState([]);
  const [selectedAppearance, setSelectedAppearance] = useState([]);
  const [selectedProperties, setSelectedProperties] = useState([]);

  // Track if initial cascade has run
  const initializedRef = useRef(false);

  // Sort years descending (most recent first)
  const sortedYears = useMemo(() => [...years].sort((a, b) => b - a), [years]);

  // Calculate all filter options based on current selections (for display in modals)
  // Cascade order: Year → Origins → Type → Variety → Size → Grade → Package → Storage → Condition → Environment → Quality → Appearance → Properties
  const availableOptions = useMemo(() => {
    if (!priceData || priceData.length === 0) {
      return { origins: [], types: [], varieties: [], sizeGroups: [], sizes: [], grades: [], packages: [],
               storages: [], conditions: [], environments: [], qualities: [],
               appearances: [], properties: [] };
    }

    let filtered = priceData;

    // Filter by year first
    if (selectedYear) {
      filtered = filtered.filter(item => item.year === selectedYear);
    }
    const originsFromData = getCleanOptions(filtered, 'origin');
    const originOptions = (origins && origins.length > 0)
      ? origins.filter((o) => originsFromData.includes(o))
      : originsFromData;

    // Filter by origins
    if (selectedOrigins.length > 0) {
      filtered = filtered.filter(item => selectedOrigins.includes(item.origin));
    }
    const types = fobCascadeOptions(filterOptions, 'type', getCleanOptions(filtered, 'type'));

    // Filter by type
    if (selectedType) {
      filtered = filtered.filter(item => item.type === selectedType);
    }
    const varieties = fobCascadeOptions(filterOptions, 'variety', getCleanOptions(filtered, 'variety'));
    filtered = applyMultiOrAllFilter(filtered, 'variety', selectedVariety, varieties);
    const sizeGroups = sizeRules ? getCleanOptions(filtered, sizeRules.readSizeGroup, commodity) : [];
    if (sizeRules) {
      filtered = applyMultiOrAllFilter(filtered, 'size_group', selectedSizeGroup, sizeGroups, (r) => readRowField(r, sizeRules.readSizeGroup));
    }
    const sizes = fobCascadeOptions(filterOptions, sizeFoKey, getCleanOptions(filtered, readSize, commodity));
    filtered = applyMultiOrAllFilter(filtered, sizeFoKey, selectedSize, sizes, (r) => readRowField(r, readSize));
    const grades = fobCascadeOptions(filterOptions, 'grade', getCleanOptions(filtered, 'grade'));
    filtered = applyMultiOrAllFilter(filtered, 'grade', selectedGrade, grades);
    const packages = fobCascadeOptions(filterOptions, 'package', getCleanOptions(filtered, 'package'));

    // Filter by package
    if (selectedPackage) {
      filtered = filtered.filter(item => item.package === selectedPackage);
    }
    const storages = fobCascadeOptions(filterOptions, 'storage', getCleanOptions(filtered, 'storage'));
    filtered = applyMultiOrAllFilter(filtered, 'storage', selectedStorage, storages);
    const conditions = fobCascadeOptions(filterOptions, 'condition', getCleanOptions(filtered, 'condition'));
    filtered = applyMultiOrAllFilter(filtered, 'condition', selectedCondition, conditions);
    const environments = fobCascadeOptions(filterOptions, 'environment', getCleanOptions(filtered, 'environment'));
    filtered = applyMultiOrAllFilter(filtered, 'environment', selectedEnvironment, environments);
    const qualities = fobCascadeOptions(filterOptions, 'quality', getCleanOptions(filtered, 'quality'));
    filtered = applyMultiOrAllFilter(filtered, 'quality', selectedQuality, qualities);
    const appearances = fobCascadeOptions(filterOptions, 'appearance', getCleanOptions(filtered, 'appearance'));
    filtered = applyMultiOrAllFilter(filtered, 'appearance', selectedAppearance, appearances);
    const properties = fobCascadeOptions(filterOptions, 'properties', getCleanOptions(filtered, 'properties'));
    filtered = applyMultiOrAllFilter(filtered, 'properties', selectedProperties, properties);

    return {
      origins: originOptions,
      types,
      varieties,
      sizeGroups,
      sizes,
      grades,
      packages,
      storages,
      conditions,
      environments,
      qualities,
      appearances,
      properties,
    };
  }, [priceData, origins, filterOptions, selectedYear, selectedOrigins, selectedType, selectedVariety, selectedSizeGroup, selectedSize, selectedGrade, selectedPackage,
      selectedStorage, selectedCondition, selectedEnvironment, selectedQuality, selectedAppearance, selectedProperties, commodity, sizeRules, readSize, sizeFoKey]);

  // Cascade function: calculates valid defaults from a starting point
  // Order: Year → Origins → Type → Variety → Size → Grade → Package → Storage → Condition → Environment → Quality → Appearance → Properties
  // The chain describes how the filter UI cascades; the values come from
  // scoring whole combinations by plotted points (see pickBestFobCombination).
  const calculateCascade = useCallback((startingData, options = {}) => {
    // Default matches this chart's initial `granularity` state, which is what
    // the init effect scores against before the user picks anything.
    const { year = null, origins = null, type = null, granularity: scoreGranularity = 'monthly' } = options;
    let filtered = startingData;

    // Apply year filter if provided
    if (year) {
      filtered = filtered.filter(item => item.year === year);
    }

    // Get available origins for this year
    const availableOrigins = getCleanOptions(filtered, 'origin');
    // If origins provided, use them; otherwise select all
    const selectedOrigs = origins || availableOrigins;
    if (selectedOrigs && selectedOrigs.length > 0) {
      filtered = filtered.filter(item => selectedOrigs.includes(item.origin));
    }

    // A caller-pinned type is honoured, but only where it has rows — pinning a
    // type the commodity does not carry empties the slice and blanks every
    // downstream default.
    if (type) {
      const withType = filtered.filter(item => item.type === type);
      if (withType.length > 0) filtered = withType;
    }

    // Score whole combinations by how many points they actually plot, rather
    // than taking the most common value one field at a time. Greedy chaining
    // optimises each field against the slice the previous one left, and over
    // eleven dependent fields it drifts onto a thin cell; it also never
    // considered leaving a mostly-blank field unset, so a rare concrete value
    // would discard the bulk of the rows.
    return {
      origins: availableOrigins,
      ...pickBestFobCombination(filtered, { granularity: scoreGranularity }),
    };
  }, []);

  const hydrateFOBByOrigin = useCallback(
    (s) => {
      initializedRef.current = true;
      if (typeof s.filtersExpanded === 'boolean') setFiltersExpanded(s.filtersExpanded);
      if (s.granularity === 'monthly' || s.granularity === 'weekly') setGranularity(s.granularity);

      const yRaw = s.selectedYear;
      const yNum = typeof yRaw === 'number' ? yRaw : Number(yRaw);
      const y =
        Number.isFinite(yNum) && sortedYears.includes(yNum) ? yNum : sortedYears[0];
      setSelectedYear(y);

      let filtered = priceData.filter((item) => item.year === y);
      const originOptsFromData = getCleanOptions(filtered, 'origin');
      const orderedOriginOpts = (origins && origins.length > 0)
        ? origins.filter((o) => originOptsFromData.includes(o))
        : originOptsFromData;
      let nextOrigins = Array.isArray(s.selectedOrigins)
        ? s.selectedOrigins.filter((o) => orderedOriginOpts.includes(o))
        : [];
      if (nextOrigins.length === 0) nextOrigins = orderedOriginOpts;
      setSelectedOrigins(nextOrigins);
      if (nextOrigins.length > 0) {
        filtered = filtered.filter((item) => nextOrigins.includes(item.origin));
      }

            // Every load opens on the combination with the most data, ignoring the
      // characteristics saved from the last visit — restoring them brought a
      // user back to whatever thin slice they last drilled into, which reads
      // as a broken chart. Filtering is a within-session action.
      const restored = pickBestFobCombination(filtered, { granularity: s.granularity === 'weekly' ? 'weekly' : 'monthly' });

      setSelectedType(restored.type);
      {
        let scoped = filtered;
        if (restored.type) scoped = scoped.filter((item) => item.type === restored.type);
        setSelectedVariety(getCleanOptions(scoped, 'variety'));
        setSelectedSize(getCleanOptions(scoped, readSize, commodity));
        setSelectedGrade(getCleanOptions(scoped, 'grade'));
        setSelectedStorage(getCleanOptions(scoped, 'storage'));
        setSelectedQuality(getCleanOptions(scoped, 'quality'));
        setSelectedProperties(getCleanOptions(scoped, 'properties'));
      }
      setSelectedPackage(restored.package);
      setSelectedCondition(restored.condition);
      setSelectedEnvironment(restored.environment);
      setSelectedAppearance(restored.appearance);
    },
    [priceData, sortedYears, origins]
  );

  const snapshotFOBByOrigin = useCallback(
    () => ({
      filtersExpanded,
      granularity,
      selectedYear,
      selectedOrigins,
      selectedType,
      selectedVariety,
      selectedSize,
      selectedGrade,
      selectedPackage,
      selectedStorage,
      selectedCondition,
      selectedEnvironment,
      selectedQuality,
      selectedAppearance,
      selectedProperties,
    }),
    [
      filtersExpanded,
      granularity,
      selectedYear,
      selectedOrigins,
      selectedType,
      selectedVariety,
      selectedSize,
      selectedGrade,
      selectedPackage,
      selectedStorage,
      selectedCondition,
      selectedEnvironment,
      selectedQuality,
      selectedAppearance,
      selectedProperties,
    ]
  );

  usePersistWidgetSnapshot(
    'fob-price-by-origin',
    commodity,
    !!(priceData && priceData.length > 0 && sortedYears.length > 0),
    hydrateFOBByOrigin,
    snapshotFOBByOrigin,
    [
      filtersExpanded,
      granularity,
      selectedYear,
      selectedOrigins,
      selectedType,
      selectedVariety,
      selectedSize,
      selectedGrade,
      selectedPackage,
      selectedStorage,
      selectedCondition,
      selectedEnvironment,
      selectedQuality,
      selectedAppearance,
      selectedProperties,
    ]
  );

  // Do NOT clear initializedRef in useEffect([commodity]) — runs after layout hydrate and retriggers
  // init cascade (same as FOB heatmap fix).

  // Default cascade only when there is no Firestore snapshot to hydrate. Gated on analytics fetch
  // so this cannot run before hydrate (same issue as FOB heatmap — would overwrite filters in UI + Firebase).
  useEffect(() => {
    if (!analyticsPrefsReady || !priceData || priceData.length === 0 || initializedRef.current) return;

    initializedRef.current = true;

    const defaultYear = sortedYears[0];
    setSelectedYear(defaultYear);

    const cascade = calculateCascade(priceData, { year: defaultYear });
    setSelectedOrigins(cascade.origins);
    setSelectedType(cascade.type);
    {
      let scoped = priceData.filter((item) => item.year === defaultYear);
      if (cascade.type) scoped = scoped.filter((item) => item.type === cascade.type);
      setSelectedVariety(getCleanOptions(scoped, 'variety'));
      setSelectedSizeGroup(sizeRules ? getCleanOptions(scoped, sizeRules.readSizeGroup, commodity) : []);
      setSelectedSize(getCleanOptions(scoped, readSize, commodity));
      setSelectedGrade(getCleanOptions(scoped, 'grade'));
      setSelectedStorage(getCleanOptions(scoped, 'storage'));
      setSelectedQuality(getCleanOptions(scoped, 'quality'));
      setSelectedProperties(getCleanOptions(scoped, 'properties'));
      setSelectedCondition(getCleanOptions(scoped, 'condition'));
      setSelectedEnvironment(getCleanOptions(scoped, 'environment'));
      setSelectedAppearance(getCleanOptions(scoped, 'appearance'));
    }
    setSelectedPackage(cascade.package);
  }, [analyticsPrefsReady, priceData, sortedYears, calculateCascade]);

  // Handler for YEAR change - cascades origins, type, and all downstream
  const handleYearChange = useCallback((newYear) => {
    const yearNum = Number(newYear);
    setSelectedYear(yearNum);

    // Calculate cascade from new year (select all origins, prefer Conventional)
    const cascade = calculateCascade(priceData, { year: yearNum });
    setSelectedOrigins(cascade.origins);
    setSelectedType(cascade.type);
    {
      let scoped = priceData.filter((item) => item.year === yearNum);
      if (cascade.type) scoped = scoped.filter((item) => item.type === cascade.type);
      setSelectedVariety(getCleanOptions(scoped, 'variety'));
      setSelectedSizeGroup(sizeRules ? getCleanOptions(scoped, sizeRules.readSizeGroup, commodity) : []);
      setSelectedSize(getCleanOptions(scoped, readSize, commodity));
      setSelectedGrade(getCleanOptions(scoped, 'grade'));
      setSelectedStorage(getCleanOptions(scoped, 'storage'));
      setSelectedQuality(getCleanOptions(scoped, 'quality'));
      setSelectedProperties(getCleanOptions(scoped, 'properties'));
      setSelectedCondition(getCleanOptions(scoped, 'condition'));
      setSelectedEnvironment(getCleanOptions(scoped, 'environment'));
      setSelectedAppearance(getCleanOptions(scoped, 'appearance'));
    }
    setSelectedPackage(cascade.package);
  }, [priceData, calculateCascade]);

  // Handler for TYPE change - cascades variety and all downstream
  const handleTypeChange = useCallback((newType) => {
    setSelectedType(newType);

    // Calculate cascade from year + origins + new type
    const cascade = calculateCascade(priceData, { year: selectedYear, origins: selectedOrigins, type: newType });
    {
      let scoped = priceData;
      if (selectedYear) scoped = scoped.filter((item) => item.year === selectedYear);
      if (selectedOrigins.length) scoped = scoped.filter((item) => selectedOrigins.includes(item.origin));
      if (newType) scoped = scoped.filter((item) => item.type === newType);
      setSelectedVariety(getCleanOptions(scoped, 'variety'));
      setSelectedSizeGroup(sizeRules ? getCleanOptions(scoped, sizeRules.readSizeGroup, commodity) : []);
      setSelectedSize(getCleanOptions(scoped, readSize, commodity));
      setSelectedGrade(getCleanOptions(scoped, 'grade'));
      setSelectedStorage(getCleanOptions(scoped, 'storage'));
      setSelectedQuality(getCleanOptions(scoped, 'quality'));
      setSelectedProperties(getCleanOptions(scoped, 'properties'));
      setSelectedCondition(getCleanOptions(scoped, 'condition'));
      setSelectedEnvironment(getCleanOptions(scoped, 'environment'));
      setSelectedAppearance(getCleanOptions(scoped, 'appearance'));
    }
    setSelectedPackage(cascade.package);
  }, [priceData, selectedYear, selectedOrigins, calculateCascade]);

  // Generic cascade handler for any filter level (variety and below)
  const handleFilterChange = useCallback((level, newValue) => {
    // Build filtered data starting from year → origins → type
    let filtered = priceData;
    if (selectedYear) filtered = filtered.filter(item => item.year === selectedYear);
    if (selectedOrigins.length > 0) filtered = filtered.filter(item => selectedOrigins.includes(item.origin));
    if (selectedType) filtered = filtered.filter(item => item.type === selectedType);

    const filterOrder = ['variety', 'sizeGroup', 'size', 'grade', 'package', 'storage', 'condition', 'environment', 'quality', 'appearance', 'properties'];
    const filterFields = {
      variety: 'variety',
      sizeGroup: 'size_group',
      size: sizeFoKey,
      grade: 'grade',
      package: 'package',
      storage: 'storage',
      condition: 'condition',
      environment: 'environment',
      quality: 'quality',
      appearance: 'appearance',
      properties: 'properties'
    };
    const currentValues = {
      variety: selectedVariety,
      sizeGroup: selectedSizeGroup,
      size: selectedSize,
      grade: selectedGrade,
      package: selectedPackage,
      storage: selectedStorage,
      condition: selectedCondition,
      environment: selectedEnvironment,
      quality: selectedQuality,
      appearance: selectedAppearance,
      properties: selectedProperties
    };
    const setters = {
      variety: setSelectedVariety,
      sizeGroup: setSelectedSizeGroup,
      size: setSelectedSize,
      grade: setSelectedGrade,
      package: setSelectedPackage,
      storage: setSelectedStorage,
      condition: setSelectedCondition,
      environment: setSelectedEnvironment,
      quality: setSelectedQuality,
      appearance: setSelectedAppearance,
      properties: setSelectedProperties
    };

    const levelIdx = filterOrder.indexOf(level);
    const readerFor = (l) => {
      if (l === 'size') return readSize;
      if (l === 'sizeGroup') return sizeRules?.readSizeGroup || 'size_group';
      return filterFields[l];
    };

    // Apply all filters up to (but not including) this level
    for (let i = 0; i < levelIdx; i++) {
      const l = filterOrder[i];
      const reader = readerFor(l);
      const value = currentValues[l];
      if (Array.isArray(value)) {
        filtered = applyMultiOrAllFilter(filtered, filterFields[l], value, getCleanOptions(filtered, reader, commodity), (r) => readRowField(r, reader));
      } else if (value) {
        filtered = filtered.filter((item) => readRowField(item, reader) === value);
      }
    }

    // Apply the new value for this level
    setters[level](newValue);

    if (Array.isArray(newValue)) {
      const reader = readerFor(level);
      filtered = applyMultiOrAllFilter(filtered, filterFields[level], newValue, getCleanOptions(filtered, reader, commodity), (r) => readRowField(r, reader));
    } else if (newValue) {
      filtered = filtered.filter((item) => readRowField(item, readerFor(level)) === newValue);
    }

    // Cascade downstream: score the remaining fields as a whole combination
    // rather than taking the most common value one at a time, so the chart
    // lands on the best-covered slice inside the value just picked.
    const best = pickBestFobCombination(filtered, { granularity, commodity });
    const multiKeys = FOB_MULTI_KEYS;
    for (let i = levelIdx + 1; i < filterOrder.length; i++) {
      const l = filterOrder[i];
      if (multiKeys.has(l)) {
        setters[l](getCleanOptions(filtered, readerFor(l), commodity));
      } else {
        setters[l](best[l] || '');
      }
    }
  }, [priceData, selectedYear, selectedOrigins, selectedType, selectedVariety, selectedSizeGroup, selectedSize, selectedGrade, selectedPackage,
      selectedStorage, selectedCondition, selectedEnvironment, selectedQuality,
      selectedAppearance, selectedProperties, granularity, commodity, readSize, sizeRules]);

  // Filter data by all selected characteristics
  const filteredPriceData = useMemo(() => {
    let rows = priceData.filter(item => {
      if (selectedYear && item.year !== selectedYear) return false;
      if (selectedType && item.type !== selectedType) return false;
      if (selectedPackage && item.package !== selectedPackage) return false;
      return true;
    });
    rows = applyMultiOrAllFilter(rows, 'variety', selectedVariety, availableOptions.varieties);
    rows = applyMultiOrAllFilter(rows, 'size_group', selectedSizeGroup, availableOptions.sizeGroups, sizeRules ? (r) => readRowField(r, sizeRules.readSizeGroup) : undefined);
    rows = applyMultiOrAllFilter(rows, 'condition', selectedCondition, availableOptions.conditions);
    rows = applyMultiOrAllFilter(rows, 'environment', selectedEnvironment, availableOptions.environments);
    rows = applyMultiOrAllFilter(rows, 'appearance', selectedAppearance, availableOptions.appearances);
    rows = applyMultiOrAllFilter(rows, sizeFoKey, selectedSize, availableOptions.sizes, (r) => readRowField(r, readSize));
    rows = applyMultiOrAllFilter(rows, 'grade', selectedGrade, availableOptions.grades);
    rows = applyMultiOrAllFilter(rows, 'storage', selectedStorage, availableOptions.storages);
    rows = applyMultiOrAllFilter(rows, 'quality', selectedQuality, availableOptions.qualities);
    rows = applyMultiOrAllFilter(rows, 'properties', selectedProperties, availableOptions.properties);
    return rows;
  }, [priceData, selectedYear, selectedType, selectedVariety, selectedSizeGroup, selectedSize, selectedGrade, selectedPackage,
      selectedStorage, selectedCondition, selectedEnvironment, selectedQuality,
      selectedAppearance, selectedProperties, availableOptions, sizeRules, readSize, sizeFoKey]);

  // Aggregate price data by period (month or week) and origin
  const chartData = useMemo(() => {
    if (!filteredPriceData || filteredPriceData.length === 0 || selectedYear === null) return [];

    const aggregated = {};
    const splitGrade = shouldNameSeriesForMulti(selectedGrade, availableOptions.grades);
    const splitSize = shouldNameSeriesForMulti(selectedSize, availableOptions.sizes);
    const splitVariety = shouldNameSeriesForMulti(selectedVariety, availableOptions.varieties);
    const splitStorage = shouldNameSeriesForMulti(selectedStorage, availableOptions.storages);
    const splitCondition = shouldNameSeriesForMulti(selectedCondition, availableOptions.conditions);
    const splitEnvironment = shouldNameSeriesForMulti(selectedEnvironment, availableOptions.environments);
    const splitQuality = shouldNameSeriesForMulti(selectedQuality, availableOptions.qualities);
    const splitAppearance = shouldNameSeriesForMulti(selectedAppearance, availableOptions.appearances);
    const splitProperties = shouldNameSeriesForMulti(selectedProperties, availableOptions.properties);
    const seriesOf = (item) => {
      const bits = [item.origin];
      if (splitVariety && item.variety) bits.push(item.variety);
      const sizeBit = readRowField(item, readSize);
      if (splitSize && sizeBit) bits.push(sizeBit);
      if (splitGrade && item.grade) bits.push(item.grade);
      if (splitStorage && item.storage) bits.push(item.storage);
      if (splitCondition && item.condition) bits.push(item.condition);
      if (splitEnvironment && item.environment) bits.push(item.environment);
      if (splitQuality && item.quality) bits.push(item.quality);
      if (splitAppearance && item.appearance) bits.push(item.appearance);
      if (splitProperties && item.properties) bits.push(item.properties);
      return bits.join(' · ');
    };
    const seriesKeys = new Set();
    filteredPriceData.forEach(item => {
      const period = granularity === 'weekly' ? item.iso_week : item.month;
      const origin = item.origin;
      const price = item.avg_price;

      if (!price || !origin || !period) return;
      if (!selectedOrigins.includes(origin)) return;
      const series = seriesOf(item);
      seriesKeys.add(series);

      if (!aggregated[period]) {
        aggregated[period] = { origins: {} };
      }
      if (!aggregated[period].origins[series]) {
        aggregated[period].origins[series] = { prices: [] };
      }
      aggregated[period].origins[series].prices.push(price);
    });
    const seriesList = [...seriesKeys].sort();

    const result = [];
    if (granularity === 'weekly') {
      Object.entries(aggregated)
        .sort(([a], [b]) => Number(a) - Number(b))
        .forEach(([week, data]) => {
          const row = { period: `W${week}`, periodNum: Number(week) };
          seriesList.forEach(origin => {
            const originData = data.origins[origin];
            if (originData && originData.prices.length > 0) {
              const avg = originData.prices.reduce((a, b) => a + b, 0) / originData.prices.length;
              row[origin] = Math.round(avg * 100) / 100;
            }
          });
          result.push(row);
        });
    } else {
      for (let month = 1; month <= 12; month++) {
        const monthLabel = MONTH_LABELS[month - 1];
        const row = { period: monthLabel, periodNum: month };

        if (aggregated[month]) {
          seriesList.forEach(origin => {
            const originData = aggregated[month].origins[origin];
            if (originData && originData.prices.length > 0) {
              const avg = originData.prices.reduce((a, b) => a + b, 0) / originData.prices.length;
              row[origin] = Math.round(avg * 100) / 100;
            }
          });
        }

        result.push(row);
      }
    }

    return result;
  }, [filteredPriceData, selectedYear, selectedOrigins, granularity, selectedGrade, selectedSize, selectedVariety,
      selectedStorage, selectedQuality, selectedProperties, availableOptions]);

  // Process chart data to add solid line and gap connector keys
  const seriesKeys = useMemo(() => {
    if (!chartData || chartData.length === 0) return [];
    const keys = new Set();
    chartData.forEach((row) => {
      Object.keys(row).forEach((k) => {
        if (k !== 'period' && k !== 'periodNum' && k !== 'origins') keys.add(k);
      });
    });
    return [...keys].sort();
  }, [chartData]);

  const { data: processedChartData, gapKeys } = useMemo(() => {
    return processChartDataForGaps(chartData, seriesKeys, granularity);
  }, [chartData, seriesKeys, granularity]);

  // Filter selectedOrigins to only include those that actually have data in the chart
  const originsWithData = useMemo(() => {
    if (!chartData || chartData.length === 0) return [];
    return seriesKeys.filter(origin => {
      // Check if any data point has a value for this origin
      return chartData.some(dataPoint =>
        dataPoint[origin] !== undefined && dataPoint[origin] !== null
      );
    });
  }, [chartData, seriesKeys]);

  const chartTooltipBoundaryRef = useRef(null);
  const { trigger: tooltipTrigger, tooltipDismissKey } = useChartTooltipTrigger(chartTooltipBoundaryRef);
  const isBelowLg = useIsBelowLg();

  const fobOriginTooltipContent = useCallback(
    (props) => (
      <FobByOriginTooltipBody
        {...props}
        theme={theme}
        fontSize={fontSize}
        originsWithData={originsWithData}
        granularity={granularity}
        selectedYear={selectedYear}
        avgPriceLabel={t('charts.fobPriceByOrigin.avgPrice')}
      />
    ),
    [theme, fontSize, originsWithData, granularity, selectedYear, t]
  );

  const handleOriginToggle = (origin) => {
    if (selectedOrigins.includes(origin)) {
      setSelectedOrigins(selectedOrigins.filter(o => o !== origin));
    } else {
      setSelectedOrigins([...selectedOrigins, origin]);
    }
  };

  const getOriginDisplayText = () => {
    if (selectedOrigins.length === 0) return t('charts.fobPriceByOrigin.selectRegion');
    return singleSelectionLabel(selectedOrigins, availableOptions.origins)
      ?? (selectedOrigins.length === availableOptions.origins.length
        ? t('charts.fobPriceByOrigin.allRegions')
        : t('filters.nSelected', { count: selectedOrigins.length }));
  };

  const helpDescription = "Line chart showing FOB price trends by origin throughout the year. Compare pricing patterns across different growing regions to identify the best value sources.";

  if (!priceData || priceData.length === 0) {
    return (
      <div className="h-full flex flex-col bg-transparent">
        <div className="border-b py-2 flex-shrink-0" style={{ borderColor: theme.borderColor }}>
          <div className="flex items-center gap-1">
            <h3 className="uppercase font-bold tracking-wide" style={{ fontSize: fontSize + 4, color: theme.titleColor }}>
              {t('charts.fobPriceByOrigin.title')}
            </h3>
            <ChartHelpButton title="FOB Price by Origin" description={helpDescription} />
          </div>
        </div>
        <div className="flex-1 flex items-center justify-center">
          <p className="text-center" style={{ fontSize, color: theme.noDataText }}>
            {t('charts.fobPriceByOrigin.noData')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col bg-transparent">
      <div className="px-4">
      <div className="border-b py-2 flex-shrink-0" style={{ borderColor: theme.borderColor }}>
        <div className="flex items-center gap-1">
          <h3 className="uppercase font-bold tracking-wide" style={{ fontSize: fontSize + 4, color: theme.titleColor }}>
            {t('charts.fobPriceByOrigin.title')} {selectedYear || '...'}
          </h3>
          <ChartHelpButton title="FOB Price by Origin" description={helpDescription} />
        </div>
      </div>

      </div>

      <div className="flex-1 flex flex-col min-h-0 px-4">
        {/* Filters Header */}
        <button
          onClick={() => setFiltersExpanded(!filtersExpanded)}
          className="w-full flex items-center justify-between py-2 border-b flex-shrink-0"
          style={{ borderColor: theme.borderSecondary }}
        >
          <span className="font-medium" style={{ fontSize: fontSize - 2, color: theme.secondaryText }}>{t('filters.filters')}</span>
          {filtersExpanded ? (
            <ChevronUp size={16} style={{ color: theme.secondaryText }} />
          ) : (
            <ChevronDown size={16} style={{ color: theme.secondaryText }} />
          )}
        </button>

        {filtersExpanded && (
          <div className="py-2 space-y-2 flex-shrink-0">
            {/* Granularity Toggle */}
            <div>
              <label className="block mb-1 font-medium" style={{ fontSize: fontSize - 2, color: theme.secondaryText }}>{t('charts.fobPriceByOrigin.granularity')}</label>
              <div className="flex gap-1">
                <button
                  onClick={() => setGranularity('monthly')}
                  style={{
                    fontSize: fontSize - 2,
                    ...(granularity !== 'monthly' ? { backgroundColor: theme.controlBg, borderColor: theme.controlBorder, color: theme.controlText } : {})
                  }}
                  className={`flex-1 py-2 px-2 rounded-lg font-medium transition-colors ${
                    granularity === 'monthly'
                      ? 'bg-green-500 text-white'
                      : 'border'
                  }`}
                >
                  {t('charts.fobPriceByOrigin.monthly')}
                </button>
                <button
                  onClick={() => setGranularity('weekly')}
                  style={{
                    fontSize: fontSize - 2,
                    ...(granularity !== 'weekly' ? { backgroundColor: theme.controlBg, borderColor: theme.controlBorder, color: theme.controlText } : {})
                  }}
                  className={`flex-1 py-2 px-2 rounded-lg font-medium transition-colors ${
                    granularity === 'weekly'
                      ? 'bg-green-500 text-white'
                      : 'border'
                  }`}
                >
                  {t('charts.fobPriceByOrigin.weekly')}
                </button>
              </div>
            </div>

            {/* Year and Region Filters */}
            <div className="grid grid-cols-2 gap-2">
              <FilterDropdown
                label={t('filters.year')}
                value={selectedYear || '-'}
                onClick={() => setYearModalOpen(true)}
              />
              <FilterDropdown
                label={t('charts.fobPriceByOrigin.regions')}
                value={getOriginDisplayText()}
                onClick={() => setOriginModalOpen(true)}
              />
            </div>

            {/* Product Characteristic Filters */}
            <div className="grid grid-cols-3 gap-2">
              {availableOptions.types.length > 0 && (
                <FilterDropdown
                  label={t('charts.fobPriceByOrigin.type')}
                  value={selectedType || t('charts.fobPriceByOrigin.select')}
                  onClick={() => setTypeModalOpen(true)}
                />
              )}
              {availableOptions.varieties.length > 0 && (
                <FilterDropdown
                  label={t('charts.fobPriceByOrigin.variety')}
                  value={multiSelectDropdownLabel(selectedVariety, availableOptions.varieties, {
                    allLabel: t('filters.allVarieties'),
                    nSelectedLabel: (n) => t('filters.nSelected', { count: n }),
                    emptyLabel: t('charts.fobPriceByOrigin.select'),
                  })}
                  onClick={() => setVarietyModalOpen(true)}
                />
              )}
              {gatedCascade && availableOptions.sizeGroups.length > 0 && (
                <FilterDropdown
                  label={t('filters.sizeGroup', 'Size Group')}
                  value={multiSelectDropdownLabel(selectedSizeGroup, availableOptions.sizeGroups, {
                    allLabel: t('filters.allSizeGroups', 'All Size Groups'),
                    nSelectedLabel: (n) => t('filters.nSelected', { count: n }),
                    emptyLabel: t('charts.fobPriceByOrigin.select'),
                  })}
                  onClick={() => setSizeGroupModalOpen(true)}
                />
              )}
              {availableOptions.sizes.length > 0 && (
                <FilterDropdown
                  label={sizeRules ? t('filters.sizeGranularity', 'Size granularity') : t('charts.fobPriceByOrigin.size')}
                  value={multiSelectDropdownLabel(selectedSize, availableOptions.sizes, {
                    allLabel: t('filters.allSizes'),
                    nSelectedLabel: (n) => t('filters.nSelected', { count: n }),
                    emptyLabel: t('charts.fobPriceByOrigin.select'),
                  })}
                  onClick={() => setSizeModalOpen(true)}
                />
              )}
              {availableOptions.grades.length > 0 && (
                <FilterDropdown
                  label={t('charts.fobPriceByOrigin.grade')}
                  value={multiSelectDropdownLabel(selectedGrade, availableOptions.grades, {
                    allLabel: t('filters.allGrades'),
                    nSelectedLabel: (n) => t('filters.nSelected', { count: n }),
                    emptyLabel: t('charts.fobPriceByOrigin.select'),
                  })}
                  onClick={() => setGradeModalOpen(true)}
                />
              )}
              {availableOptions.packages.length > 0 && (
                <FilterDropdown
                  label={t('charts.fobPriceByOrigin.package')}
                  value={selectedPackage || t('charts.fobPriceByOrigin.select')}
                  onClick={() => setPackageModalOpen(true)}
                />
              )}
              {availableOptions.storages.length > 0 && (
                <FilterDropdown
                  label={t('charts.fobPriceByOrigin.storage')}
                  value={multiSelectDropdownLabel(selectedStorage, availableOptions.storages, {
                    allLabel: t('filters.allStorage'),
                    nSelectedLabel: (n) => t('filters.nSelected', { count: n }),
                    emptyLabel: t('charts.fobPriceByOrigin.select'),
                  })}
                  onClick={() => setStorageModalOpen(true)}
                />
              )}
              {availableOptions.conditions.length > 0 && (
                <FilterDropdown
                  label={t('charts.fobPriceByOrigin.condition')}
                  value={multiSelectDropdownLabel(selectedCondition, availableOptions.conditions, {
                    allLabel: t('filters.allCondition', 'All Conditions'),
                    nSelectedLabel: (n) => t('filters.nSelected', { count: n }),
                    emptyLabel: t('charts.fobPriceByOrigin.select'),
                  })}
                  onClick={() => setConditionModalOpen(true)}
                />
              )}
              {availableOptions.environments.length > 0 && (
                <FilterDropdown
                  label={t('charts.fobPriceByOrigin.environment')}
                  value={multiSelectDropdownLabel(selectedEnvironment, availableOptions.environments, {
                    allLabel: t('filters.allEnvironment', 'All Environments'),
                    nSelectedLabel: (n) => t('filters.nSelected', { count: n }),
                    emptyLabel: t('charts.fobPriceByOrigin.select'),
                  })}
                  onClick={() => setEnvironmentModalOpen(true)}
                />
              )}
              {availableOptions.qualities.length > 0 && (
                <FilterDropdown
                  label={t('charts.fobPriceByOrigin.quality')}
                  value={multiSelectDropdownLabel(selectedQuality, availableOptions.qualities, {
                    allLabel: t('filters.allQuality'),
                    nSelectedLabel: (n) => t('filters.nSelected', { count: n }),
                    emptyLabel: t('charts.fobPriceByOrigin.select'),
                  })}
                  onClick={() => setQualityModalOpen(true)}
                />
              )}
              {availableOptions.appearances.length > 0 && (
                <FilterDropdown
                  label={t('charts.fobPriceByOrigin.appearance')}
                  value={multiSelectDropdownLabel(selectedAppearance, availableOptions.appearances, {
                    allLabel: t('filters.allAppearance', 'All Appearances'),
                    nSelectedLabel: (n) => t('filters.nSelected', { count: n }),
                    emptyLabel: t('charts.fobPriceByOrigin.select'),
                  })}
                  onClick={() => setAppearanceModalOpen(true)}
                />
              )}
              {availableOptions.properties.length > 0 && (
                <FilterDropdown
                  label={t('charts.fobPriceByOrigin.properties')}
                  value={multiSelectDropdownLabel(selectedProperties, availableOptions.properties, {
                    allLabel: t('filters.allProperties'),
                    nSelectedLabel: (n) => t('filters.nSelected', { count: n }),
                    emptyLabel: t('charts.fobPriceByOrigin.select'),
                  })}
                  onClick={() => setPropertiesModalOpen(true)}
                />
              )}
            </div>
          </div>
        )}

        {/* Line Chart */}
        <div className="flex-1 min-h-0" ref={chartTooltipBoundaryRef}>
          {processedChartData.length > 0 && selectedOrigins.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={processedChartData}
                margin={getResponsiveChartMargin({ isBelowLg })}
              >
                <CartesianGrid strokeDasharray="3 3" stroke={theme.gridColor} />
                <XAxis
                  dataKey="period"
                  stroke={theme.axisStroke}
                  tick={{ fontSize: fontSize - 2, fill: theme.tickFill }}
                  angle={granularity === 'weekly' ? -45 : 0}
                  textAnchor={granularity === 'weekly' ? 'end' : 'middle'}
                />
                <YAxis
                  stroke={theme.axisStroke}
                  tick={{ fontSize, fill: theme.tickFill }}
                  domain={getSmartYDomain(chartData, seriesKeys)}
                  allowDataOverflow={false}
                  tickFormatter={(v) => `$${v}`}
                />
                <Tooltip
                  key={tooltipDismissKey}
                  trigger={tooltipTrigger}
                  throttleDelay={50}
                  content={fobOriginTooltipContent}
                />
                <Legend
                  verticalAlign="bottom"
                  wrapperStyle={getLegendWrapperStyle({ fontSize: fontSize - 2, paddingTop: 0, bottom: 6 })}
                  payload={originsWithData.map((origin, idx) => ({
                    value: origin,
                    type: 'line',
                    color: getChartColor(seriesKeys.indexOf(origin), seriesKeys.length)
                  }))}
                />
                {/* Solid lines for consecutive data points */}
                {seriesKeys.map((origin, originIdx) => {
                  const color = getChartColor(originIdx, seriesKeys.length);
                  return (
                    <Line
                      key={`${origin}-solid`}
                      type="monotone"
                      dataKey={`${origin}_solid`}
                      stroke={color}
                      strokeWidth={2}
                      dot={false}
                      connectNulls={false}
                      legendType="none"
                      name={`${origin}_solid`}
                    />
                  );
                })}
                {/* Dashed gap connector lines */}
                {seriesKeys.flatMap((origin, originIdx) => {
                  const color = getChartColor(originIdx, seriesKeys.length);
                  const originGapKeys = gapKeys[origin] || [];

                  return originGapKeys.map((gapKey) => (
                    <Line
                      key={gapKey}
                      type="monotone"
                      dataKey={gapKey}
                      stroke={color}
                      strokeWidth={2}
                      strokeDasharray="5 5"
                      dot={false}
                      connectNulls={true}
                      legendType="none"
                      name={gapKey}
                    />
                  ));
                })}
                {/* Dots layer - always show dots at data points */}
                {seriesKeys.map((origin, originIdx) => {
                  const color = getChartColor(originIdx, seriesKeys.length);
                  return (
                    <Line
                      key={`${origin}-dots`}
                      type="monotone"
                      dataKey={origin}
                      stroke="transparent"
                      strokeWidth={0}
                      dot={{
                        r: 4,
                        fill: color,
                        strokeWidth: 0
                      }}
                      activeDot={{
                        r: 6,
                        fill: color,
                        strokeWidth: 2,
                        stroke: '#fff'
                      }}
                      connectNulls={false}
                      legendType="none"
                      name={origin}
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-sm" style={{ color: theme.secondaryText }}>
                {selectedYear === null ? t('common.loading') : selectedOrigins.length === 0 ? t('charts.fobPriceByOrigin.selectAtLeastOneRegion') : t('charts.fobPriceByOrigin.noDataForFilters')}
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Origin Modal - Multi-select, cascades type and downstream on close */}
      <FilterModal
        isOpen={originModalOpen}
        onClose={() => {
          setOriginModalOpen(false);
          // Cascade type and downstream based on new origins selection
          if (selectedOrigins.length > 0) {
            const cascade = calculateCascade(priceData, { year: selectedYear, origins: selectedOrigins });
            setSelectedType(cascade.type);
            {
              let scoped = priceData;
              if (selectedYear) scoped = scoped.filter((item) => item.year === selectedYear);
              scoped = scoped.filter((item) => selectedOrigins.includes(item.origin));
              if (cascade.type) scoped = scoped.filter((item) => item.type === cascade.type);
              setSelectedVariety(getCleanOptions(scoped, 'variety'));
              setSelectedSizeGroup(sizeRules ? getCleanOptions(scoped, sizeRules.readSizeGroup, commodity) : []);
              setSelectedSize(getCleanOptions(scoped, readSize, commodity));
              setSelectedGrade(getCleanOptions(scoped, 'grade'));
              setSelectedStorage(getCleanOptions(scoped, 'storage'));
              setSelectedQuality(getCleanOptions(scoped, 'quality'));
              setSelectedProperties(getCleanOptions(scoped, 'properties'));
              setSelectedCondition(getCleanOptions(scoped, 'condition'));
              setSelectedEnvironment(getCleanOptions(scoped, 'environment'));
              setSelectedAppearance(getCleanOptions(scoped, 'appearance'));
            }
            setSelectedPackage(cascade.package);
          }
        }}
        title={t('charts.fobPriceByOrigin.selectRegions')}
        options={availableOptions.origins}
        selectedValues={selectedOrigins}
        onSelect={handleOriginToggle}
        multiSelect
        onSelectAll={() => setSelectedOrigins([...availableOptions.origins])}
        onClearAll={() => setSelectedOrigins([])}
      />

      {/* Year Modal - cascades to origins, type, and all downstream */}
      <FilterModal
        isOpen={yearModalOpen}
        onClose={() => setYearModalOpen(false)}
        title={t('charts.fobPriceByOrigin.selectYear')}
        options={sortedYears.map(String)}
        selectedValues={selectedYear ? String(selectedYear) : ''}
        onSelect={handleYearChange}
      />

      {/* Type Modal - cascades variety and all downstream */}
      <FilterModal
        isOpen={typeModalOpen}
        onClose={() => setTypeModalOpen(false)}
        title={t('charts.fobPriceByOrigin.selectType')}
        options={availableOptions.types}
        selectedValues={selectedType}
        onSelect={handleTypeChange}
      />

      {/* Other filters use generic cascade handler */}
      <FilterModal
        isOpen={varietyModalOpen}
        onClose={() => setVarietyModalOpen(false)}
        title={t('charts.fobPriceByOrigin.selectVariety')}
        options={availableOptions.varieties}
        selectedValues={selectedVariety}
        onSelect={(value) => {
          const next = toggleMultiValue(value, selectedVariety);
          handleFilterChange('variety', next.length ? next : availableOptions.varieties);
        }}
        multiSelect
        onSelectAll={() => handleFilterChange('variety', availableOptions.varieties)}
        onClearAll={() => handleFilterChange('variety', availableOptions.varieties)}
      />

      <FilterModal
        isOpen={sizeGroupModalOpen}
        onClose={() => setSizeGroupModalOpen(false)}
        title={t('filters.selectSizeGroup', 'Select Size Group')}
        options={availableOptions.sizeGroups}
        selectedValues={selectedSizeGroup}
        onSelect={(value) => {
          const next = toggleMultiValue(value, selectedSizeGroup);
          handleFilterChange('sizeGroup', next.length ? next : availableOptions.sizeGroups);
        }}
        multiSelect
        onSelectAll={() => handleFilterChange('sizeGroup', availableOptions.sizeGroups)}
        onClearAll={() => handleFilterChange('sizeGroup', availableOptions.sizeGroups)}
        displayFormatter={gatedCascade ? initCap : undefined}
      />
      <FilterModal
        isOpen={sizeModalOpen}
        onClose={() => setSizeModalOpen(false)}
        title={t('charts.fobPriceByOrigin.selectSize')}
        options={availableOptions.sizes}
        selectedValues={selectedSize}
        onSelect={(value) => {
          const next = toggleMultiValue(value, selectedSize);
          handleFilterChange('size', next.length ? next : availableOptions.sizes);
        }}
        multiSelect
        onSelectAll={() => handleFilterChange('size', availableOptions.sizes)}
        onClearAll={() => handleFilterChange('size', availableOptions.sizes)}
      />

      <FilterModal
        isOpen={gradeModalOpen}
        onClose={() => setGradeModalOpen(false)}
        title={t('charts.fobPriceByOrigin.selectGrade')}
        options={availableOptions.grades}
        selectedValues={selectedGrade}
        onSelect={(value) => {
          const next = toggleMultiValue(value, selectedGrade);
          handleFilterChange('grade', next.length ? next : availableOptions.grades);
        }}
        multiSelect
        onSelectAll={() => handleFilterChange('grade', availableOptions.grades)}
        onClearAll={() => handleFilterChange('grade', availableOptions.grades)}
      />

      <FilterModal
        isOpen={packageModalOpen}
        onClose={() => setPackageModalOpen(false)}
        title={t('charts.fobPriceByOrigin.selectPackage')}
        options={availableOptions.packages}
        selectedValues={selectedPackage}
        onSelect={(value) => handleFilterChange('package', value)}
      />

      <FilterModal
        isOpen={storageModalOpen}
        onClose={() => setStorageModalOpen(false)}
        title={t('charts.fobPriceByOrigin.selectStorage')}
        options={availableOptions.storages}
        selectedValues={selectedStorage}
        onSelect={(value) => {
          const next = toggleMultiValue(value, selectedStorage);
          handleFilterChange('storage', next.length ? next : availableOptions.storages);
        }}
        multiSelect
        onSelectAll={() => handleFilterChange('storage', availableOptions.storages)}
        onClearAll={() => handleFilterChange('storage', availableOptions.storages)}
      />

      <FilterModal
        isOpen={conditionModalOpen}
        onClose={() => setConditionModalOpen(false)}
        title={t('charts.fobPriceByOrigin.selectCondition')}
        options={availableOptions.conditions}
        selectedValues={selectedCondition}
        onSelect={(value) => {
          const next = toggleMultiValue(value, selectedCondition);
          handleFilterChange('condition', next.length ? next : availableOptions.conditions);
        }}
        multiSelect
        onSelectAll={() => handleFilterChange('condition', availableOptions.conditions)}
        onClearAll={() => handleFilterChange('condition', availableOptions.conditions)}
      />

      <FilterModal
        isOpen={environmentModalOpen}
        onClose={() => setEnvironmentModalOpen(false)}
        title={t('charts.fobPriceByOrigin.selectEnvironment')}
        options={availableOptions.environments}
        selectedValues={selectedEnvironment}
        onSelect={(value) => {
          const next = toggleMultiValue(value, selectedEnvironment);
          handleFilterChange('environment', next.length ? next : availableOptions.environments);
        }}
        multiSelect
        onSelectAll={() => handleFilterChange('environment', availableOptions.environments)}
        onClearAll={() => handleFilterChange('environment', availableOptions.environments)}
      />

      <FilterModal
        isOpen={qualityModalOpen}
        onClose={() => setQualityModalOpen(false)}
        title={t('charts.fobPriceByOrigin.selectQuality')}
        options={availableOptions.qualities}
        selectedValues={selectedQuality}
        onSelect={(value) => {
          const next = toggleMultiValue(value, selectedQuality);
          handleFilterChange('quality', next.length ? next : availableOptions.qualities);
        }}
        multiSelect
        onSelectAll={() => handleFilterChange('quality', availableOptions.qualities)}
        onClearAll={() => handleFilterChange('quality', availableOptions.qualities)}
      />

      <FilterModal
        isOpen={appearanceModalOpen}
        onClose={() => setAppearanceModalOpen(false)}
        title={t('charts.fobPriceByOrigin.selectAppearance')}
        options={availableOptions.appearances}
        selectedValues={selectedAppearance}
        onSelect={(value) => {
          const next = toggleMultiValue(value, selectedAppearance);
          handleFilterChange('appearance', next.length ? next : availableOptions.appearances);
        }}
        multiSelect
        onSelectAll={() => handleFilterChange('appearance', availableOptions.appearances)}
        onClearAll={() => handleFilterChange('appearance', availableOptions.appearances)}
      />

      <FilterModal
        isOpen={propertiesModalOpen}
        onClose={() => setPropertiesModalOpen(false)}
        title={t('charts.fobPriceByOrigin.selectProperties')}
        options={availableOptions.properties}
        selectedValues={selectedProperties}
        onSelect={(value) => {
          const next = toggleMultiValue(value, selectedProperties);
          handleFilterChange('properties', next.length ? next : availableOptions.properties);
        }}
        multiSelect
        onSelectAll={() => handleFilterChange('properties', availableOptions.properties)}
        onClearAll={() => handleFilterChange('properties', availableOptions.properties)}
      />
    </div>
  );
};

export default FOBPriceByOriginChart;
